import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

/** Sayt sahifalari. Yangi sahifa qo'shish: shu tipga variant qo'shiladi. */
export type Route =
  | { name: "home" }
  | { name: "posts" }
  | { name: "post"; slug: string }
  | { name: "winners" };

const PATHS = { home: "/", posts: "/elonlar", winners: "/goliblar" } as const;

function parse(pathname: string): Route {
  const clean = pathname.replace(/\/+$/, "") || "/";
  if (clean === PATHS.posts) return { name: "posts" };
  if (clean === PATHS.winners) return { name: "winners" };

  const post = clean.match(/^\/elonlar\/([a-z0-9-]+)$/);
  if (post?.[1]) return { name: "post", slug: post[1] };

  return { name: "home" };
}

export function hrefFor(route: Route): string {
  switch (route.name) {
    case "posts":
      return PATHS.posts;
    case "post":
      return `${PATHS.posts}/${route.slug}`;
    case "winners":
      return PATHS.winners;
    default:
      return PATHS.home;
  }
}

interface RouterValue {
  route: Route;
  go: (route: Route) => void;
}

const RouterContext = createContext<RouterValue | null>(null);

/**
 * Kichik manzil boshqaruvi. Haqiqiy yo'llar (hash emas) — sahifa havolasi
 * ulashishga yaroqli bo'lishi va qidiruv tizimlari uni ko'rishi kerak.
 * Nginx noma'lum yo'lni `index.html` ga qaytaradi.
 */
export function RouterProvider({ children }: { children: ReactNode }) {
  const [route, setRoute] = useState<Route>(() => parse(window.location.pathname));

  useEffect(() => {
    const onPop = () => setRoute(parse(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const go = useCallback((next: Route) => {
    window.history.pushState(null, "", hrefFor(next));
    setRoute(next);
    window.scrollTo({ top: 0 });
  }, []);

  const value = useMemo(() => ({ route, go }), [route, go]);
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRouter(): RouterValue {
  const value = useContext(RouterContext);
  if (!value) throw new Error("useRouter faqat RouterProvider ichida ishlaydi");
  return value;
}

/**
 * Ichki havola. Oddiy `<a>` bo'lib qoladi (yangi oynada ochish, nusxalash
 * ishlaydi), lekin oddiy bosishda sahifa qayta yuklanmaydi.
 */
export function Link({
  to,
  className,
  children,
}: {
  to: Route;
  className?: string;
  children: ReactNode;
}) {
  const { go } = useRouter();
  return (
    <a
      href={hrefFor(to)}
      className={className}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        go(to);
      }}
    >
      {children}
    </a>
  );
}
